/**
 * Status of the server-side recalc-all runs (`/api/v2/admin/recalc/:kind`).
 *
 * One shared state per kind, so the admin card, the hubs page and the badges
 * in the module tables show the same run without polling twice. Polling runs
 * while at least one component is subscribed: every 3 s while a run is going,
 * every 60 s otherwise. The run itself lives on the server — closing the page
 * does not stop it.
 */
import { computed, onBeforeUnmount, onMounted, ref, type Ref } from 'vue'
import http from '@/libs/http'

export type RecalcKind = 'modules' | 'graphs'
export type RecalcItemState = 'waiting' | 'running' | 'done' | 'failed'

export interface RecalcItem {
	key: string
	label: string
	group?: string
	groupLabel?: string
	state: RecalcItemState
	error?: string
	startedAt?: string
	finishedAt?: string
	ms?: number
	lastCalculated?: string | null
}

export interface RecalcStatus {
	kind: RecalcKind
	running: boolean
	cancelRequested: boolean
	cancelled: boolean
	interrupted: boolean
	startedAt: string | null
	finishedAt: string | null
	startedBy: string | null
	pauseMs: number
	total: number
	done: number
	failed: number
	currentKey: string | null
	error: string | null
	items: RecalcItem[]
}

const POLL_RUNNING_MS = 3_000
const POLL_IDLE_MS = 60_000

type Shared = {
	status: Ref<RecalcStatus | null>
	loadError: Ref<string | null>
	busy: Ref<boolean>
	subscribers: number
	timer: ReturnType<typeof setTimeout> | null
}

const shared: Partial<Record<RecalcKind, Shared>> = {}

const getShared = (kind: RecalcKind): Shared => {
	if (!shared[kind]) {
		shared[kind] = { status: ref(null), loadError: ref(null), busy: ref(false), subscribers: 0, timer: null }
	}
	return shared[kind]!
}

export const useRecalcAll = (kind: RecalcKind) => {
	const s = getShared(kind)
	const base = `/api/v2/admin/recalc/${kind}`

	const schedule = () => {
		if (s.timer) clearTimeout(s.timer)
		s.timer = null
		if (s.subscribers <= 0) return
		s.timer = setTimeout(refresh, s.status.value?.running ? POLL_RUNNING_MS : POLL_IDLE_MS)
	}

	async function refresh() {
		try {
			s.status.value = await http.getAuth<RecalcStatus>(base)
			s.loadError.value = null
		} catch (e: any) {
			s.loadError.value = e?.message || String(e)
		} finally {
			schedule()
		}
	}

	const start = async (body: { ids?: string[] } = {}) => {
		if (s.busy.value) return
		s.busy.value = true
		try {
			s.status.value = await http.postAuth<RecalcStatus>(base, body)
			s.loadError.value = null
		} catch (e: any) {
			s.loadError.value = e?.message || String(e)
			// 409: someone else started one — show that run.
			await refresh()
		} finally {
			s.busy.value = false
			schedule()
		}
	}

	const cancel = async () => {
		if (s.busy.value) return
		s.busy.value = true
		try {
			const resp = await http.postAuth<{ cancelled: boolean; status: RecalcStatus }>(`${base}/cancel`, {})
			s.status.value = resp.status
		} catch (e: any) {
			s.loadError.value = e?.message || String(e)
		} finally {
			s.busy.value = false
			schedule()
		}
	}

	const itemsByKey = computed<Record<string, RecalcItem>>(() => {
		const out: Record<string, RecalcItem> = {}
		for (const it of s.status.value?.items ?? []) out[it.key] = it
		return out
	})

	const current = computed(() => {
		const key = s.status.value?.currentKey
		return key ? itemsByKey.value[key] ?? null : null
	})

	onMounted(() => {
		s.subscribers++
		// First subscriber fetches; the others (one per hub table) share it.
		if (s.subscribers === 1) void refresh()
	})
	onBeforeUnmount(() => {
		s.subscribers--
		if (s.subscribers <= 0) {
			s.subscribers = 0
			if (s.timer) clearTimeout(s.timer)
			s.timer = null
		}
	})

	return {
		status: s.status,
		loadError: s.loadError,
		busy: s.busy,
		itemsByKey,
		current,
		refresh,
		start,
		cancel,
	}
}
